/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAuthorizationCodeForm.ts (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Authorization Code form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createAuthorizationCode, updateAuthorizationCode } from '../api/authorizationCodesApi';
import { FormState } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import { useContextStore } from '../../../store/context';

/**
 * Hook for managing authorization code form
 */
export function useAuthorizationCodeForm(
  id: string | undefined,
  duplicateId: string | null,
  tenantSlug: string,
  initialData: FormState | null
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { selectedDomain } = useContextStore();

  const isEditMode = Boolean(id && id !== 'new');
  const isDuplicateMode = Boolean(duplicateId);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(defaultFormState);
  const [originalData, setOriginalData] = useState<FormState>(defaultFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);
  const lastInitialDataRef = useRef<FormState | null>(null);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData) {
      const initialDataStr = JSON.stringify(initialData);
      const lastDataStr = lastInitialDataRef.current ? JSON.stringify(lastInitialDataRef.current) : null;

      if (initialDataStr !== lastDataStr) {
        lastInitialDataRef.current = initialData;
        setFormData(initialData);
        setOriginalData(initialData);
        initializedRef.current = true;
      }
    } else if (!isEditMode && !isDuplicateMode && !initializedRef.current) {
      setFormData(defaultFormState);
      setOriginalData(defaultFormState);
      initializedRef.current = true;
    }
  }, [initialData, isEditMode, isDuplicateMode, defaultFormState]);

  const handleChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field as string]) return prev;
      const updated = { ...prev };
      delete updated[field as string];
      return updated;
    });
  }, []);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }
    return (
      formData.code.trim() !== originalData.code.trim() ||
      formData.alias.trim() !== originalData.alias.trim() ||
      formData.description.trim() !== originalData.description.trim() ||
      formData.cosId !== originalData.cosId ||
      formData.active !== originalData.active
    );
  }, [formData, originalData, isEditMode]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async (): Promise<FormState & { id?: number; uuid?: string } | undefined> => {
    const domainId = selectedDomain?.id;
    const validationErrors = validateForm(formData, domainId);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError('Please fix the validation errors');
      return undefined;
    }

    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        code: formData.code.trim(),
        alias: formData.alias.trim() || null,
        description: formData.description.trim() || null,
        cosId: formData.cosId!,
        active: formData.active
      };

      if (!isEditMode || isDuplicateMode) {
        payload.domainId = domainId;
      }

      const result = !isEditMode || isDuplicateMode
        ? await createAuthorizationCode(payload, tenantSlug)
        : await updateAuthorizationCode(id!, payload, tenantSlug);

      showSuccess('Authorization code saved successfully');

      const savedData: FormState = {
        code: formData.code.trim(),
        alias: formData.alias.trim(),
        description: formData.description.trim(),
        cosId: formData.cosId!,
        active: formData.active
      };

      setOriginalData(savedData);

      // Return data with ID for navigation
      return { ...savedData, id: result?.id, uuid: result?.uuid } as any;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save authorization code';
      showError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isDuplicateMode, isEditMode, selectedDomain?.id, showError, showInfo, showSuccess, tenantSlug]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSave
  };
}

